import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, X } from "lucide-react";
import {
  FALLBACK_STOCK_UNIVERSE_DATA,
  loadStockUniverse,
  StockUniverseData,
} from "../../services/StockUniverseService";
import { SymbolLogo } from "./SymbolLogo";
import styles from "./TickerDropdown.module.css";

export type TickerDropdownCategoryType = "sector" | "industry";

export interface TickerDropdownCategorySelection {
  type: TickerDropdownCategoryType;
  name: string;
  symbols: string[];
}

interface CategoryOption extends TickerDropdownCategorySelection {
  label: string;
}

interface TickerDropdownProps {
  value: string;
  onChange: (value: string) => void;
  onCommit?: (value: string) => void;
  enableCategorySelection?: boolean;
  onCategorySelect?: (selection: TickerDropdownCategorySelection) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  allowClear?: boolean;
  testId?: string;
}

const MAX_RESULTS_WITHOUT_SEARCH = 50;
const MAX_RESULTS_WITH_SEARCH = 100;
const DEFAULT_STOCK_RESULTS_WITHOUT_SEARCH = 40;
const DEFAULT_ETF_RESULTS_WITHOUT_SEARCH = 10;
const MAX_CATEGORY_RESULTS_WITHOUT_SEARCH = 40;
const MAX_CATEGORY_RESULTS_WITH_SEARCH = 80;
const MENU_MAX_HEIGHT = 340;
const MENU_MIN_WIDTH = 260;
const VIEWPORT_MARGIN = 8;
const TRIGGER_GAP = 4;

export const TickerDropdown: React.FC<TickerDropdownProps> = ({
  value,
  onChange,
  onCommit,
  enableCategorySelection = false,
  onCategorySelect,
  placeholder = "Select symbol",
  className = "",
  disabled = false,
  allowClear = true,
  testId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [stockUniverse, setStockUniverse] = useState<StockUniverseData>(FALLBACK_STOCK_UNIVERSE_DATA);
  const [isUniverseLoading, setIsUniverseLoading] = useState(true);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: MENU_MIN_WIDTH,
    maxHeight: MENU_MAX_HEIGHT,
  });

  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const allowCategorySelection = enableCategorySelection && typeof onCategorySelect === "function";
  const normalizedValue = value.trim().toUpperCase();

  useEffect(() => {
    let isMounted = true;

    loadStockUniverse()
      .then((universe) => {
        if (isMounted) {
          setStockUniverse(universe);
        }
      })
      .catch(() => {
        // Keep using fallback list when the stock universe asset is unavailable.
      })
      .finally(() => {
        if (isMounted) {
          setIsUniverseLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const selectedEntry = normalizedValue ? stockUniverse.stocks[normalizedValue] : undefined;

  useEffect(() => {
    if (isUniverseLoading || !normalizedValue) return;
    if (stockUniverse.stocks[normalizedValue]) return;
    onChange("");
    onCommit?.("");
  }, [isUniverseLoading, normalizedValue, stockUniverse.stocks, onChange, onCommit]);

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    // The menu is position: fixed, so viewport coordinates are used as-is. Adding scroll offsets
    // here pushed it down the page by however far the trigger had been scrolled.
    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const width = Math.max(rect.width, MENU_MIN_WIDTH);
      const spaceBelow = window.innerHeight - rect.bottom - TRIGGER_GAP - VIEWPORT_MARGIN;
      const spaceAbove = rect.top - TRIGGER_GAP - VIEWPORT_MARGIN;
      const openUpward = spaceBelow < Math.min(MENU_MAX_HEIGHT, spaceAbove);
      const maxHeight = Math.max(
        Math.min(MENU_MAX_HEIGHT, openUpward ? spaceAbove : spaceBelow),
        120,
      );

      setDropdownPosition({
        top: openUpward
          ? Math.max(VIEWPORT_MARGIN, rect.top - maxHeight - TRIGGER_GAP)
          : rect.bottom + TRIGGER_GAP,
        left: Math.min(
          Math.max(VIEWPORT_MARGIN, rect.left),
          Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN),
        ),
        width,
        maxHeight,
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const timeoutId = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isOpen) return;
      const target = event.target as Node;
      if (
        dropdownRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
      setSearchTerm("");
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const orderedSymbols = useMemo(
    () =>
      Object.entries(stockUniverse.stocks).sort(([, left], [, right]) => {
        return left.rank - right.rank;
      }),
    [stockUniverse.stocks]
  );

  const categoryOptions = useMemo<CategoryOption[]>(() => {
    if (!allowCategorySelection) {
      return [];
    }

    const sectorOptions = Object.entries(stockUniverse.sectorToStocks).map(([name, symbols]) => ({
      type: "sector" as const,
      name,
      symbols,
      label: "Sector",
    }));

    const industryOptions = Object.entries(stockUniverse.industryToStocks).map(([name, symbols]) => ({
      type: "industry" as const,
      name,
      symbols,
      label: "Industry",
    }));

    return [...sectorOptions, ...industryOptions]
      .filter((option) => option.symbols.length > 0)
      .sort((left, right) => {
        if (left.type !== right.type) {
          return left.type === "sector" ? -1 : 1;
        }

        const countDiff = right.symbols.length - left.symbols.length;
        return countDiff !== 0 ? countDiff : left.name.localeCompare(right.name);
      });
  }, [allowCategorySelection, stockUniverse.industryToStocks, stockUniverse.sectorToStocks]);

  const filteredCategories = useMemo(() => {
    if (!allowCategorySelection) {
      return [];
    }

    const query = searchTerm.trim().toLowerCase();
    const maxResults = query ? MAX_CATEGORY_RESULTS_WITH_SEARCH : MAX_CATEGORY_RESULTS_WITHOUT_SEARCH;

    if (!query) {
      return categoryOptions.slice(0, maxResults);
    }

    return categoryOptions
      .filter((option) => {
        const typeText = option.type === "industry" ? "industry group" : "sector";
        return (
          option.name.toLowerCase().includes(query) ||
          option.type.includes(query) ||
          typeText.includes(query)
        );
      })
      .slice(0, maxResults);
  }, [allowCategorySelection, categoryOptions, searchTerm]);

  const filteredSymbols = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const maxResults = query ? MAX_RESULTS_WITH_SEARCH : MAX_RESULTS_WITHOUT_SEARCH;

    if (!query) {
      const defaultRows: Array<[string, StockUniverseData["stocks"][string]]> = [];
      const seen = new Set<string>();
      const stocksOnly = orderedSymbols.filter(([, entry]) => entry.assetType !== "etf");
      const etfsOnly = orderedSymbols.filter(([, entry]) => entry.assetType === "etf");

      for (const [symbol, entry] of stocksOnly.slice(0, DEFAULT_STOCK_RESULTS_WITHOUT_SEARCH)) {
        defaultRows.push([symbol, entry]);
        seen.add(symbol);
      }

      for (const [symbol, entry] of etfsOnly.slice(0, DEFAULT_ETF_RESULTS_WITHOUT_SEARCH)) {
        defaultRows.push([symbol, entry]);
        seen.add(symbol);
      }

      if (defaultRows.length < maxResults) {
        for (const [symbol, entry] of orderedSymbols) {
          if (seen.has(symbol)) continue;
          defaultRows.push([symbol, entry]);
          if (defaultRows.length >= maxResults) break;
        }
      }

      return defaultRows.slice(0, maxResults);
    }

    return orderedSymbols
      .filter(([symbol, entry]) => {
        const sectorText = entry.sectors.join(" ").toLowerCase();
        return (
          symbol.toLowerCase().includes(query) ||
          entry.name.toLowerCase().includes(query) ||
          sectorText.includes(query)
        );
      })
      .slice(0, maxResults);
  }, [orderedSymbols, searchTerm]);

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchTerm("");
  };

  const commitSymbol = (rawValue: string) => {
    const symbol = rawValue.trim().toUpperCase();
    if (!symbol || !(symbol in stockUniverse.stocks)) return;
    onChange(symbol);
    onCommit?.(symbol);
    closeDropdown();
  };

  const handleItemClick = (event: React.MouseEvent, symbol: string) => {
    event.preventDefault();
    event.stopPropagation();
    commitSymbol(symbol);
  };

  const handleCategoryClick = (event: React.MouseEvent, option: CategoryOption) => {
    event.preventDefault();
    event.stopPropagation();
    onCategorySelect?.({
      type: option.type,
      name: option.name,
      symbols: option.symbols,
    });
    onChange("");
    onCommit?.("");
    closeDropdown();
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeDropdown();
      return;
    }

    if (event.key === "Enter" && filteredSymbols.length > 0) {
      event.preventDefault();
      commitSymbol(filteredSymbols[0][0]);
    }
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
      event.preventDefault();
      setIsOpen(true);
    }
  };

  const handleTriggerClick = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onChange("");
    onCommit?.("");
    closeDropdown();
  };

  return (
    <div className={`${styles.dropdownWrapper} ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        data-testid={testId}
      >
        <div className={styles.triggerContent}>
          {normalizedValue ? (
            <span className={styles.selectedSummary}>
              <SymbolLogo symbol={normalizedValue} logoUrl={selectedEntry?.logoUrl} size={16} />
              <span className={styles.selectedText}>
                <span className={styles.selectedSymbol}>{normalizedValue}</span>
                <span className={styles.selectedName}>{selectedEntry?.name || ""}</span>
              </span>
            </span>
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </div>
        <ChevronDown className={`${styles.chevron} ${isOpen ? styles.rotated : ""}`} />
      </button>

      {allowClear && normalizedValue && !disabled && (
        <button
          type="button"
          className={styles.clearButton}
          aria-label="Clear symbol selection"
          onClick={handleClear}
        >
          <X className={styles.clearIcon} />
        </button>
      )}

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className={styles.dropdownMenu}
            style={{
              position: "fixed",
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              maxHeight: `${dropdownPosition.maxHeight}px`,
            }}
          >
            <div className={styles.searchRow}>
              <input
                ref={searchInputRef}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={handleSearchKeyDown}
                className={styles.searchInput}
                placeholder="Search symbol or company..."
                data-testid={testId ? `${testId}-search` : undefined}
              />
            </div>

            {isUniverseLoading && (
              <div className={styles.dropdownStatus}>Loading symbols...</div>
            )}

            {filteredSymbols.length > 0 && (
              <div className={styles.groupLabel}>Symbols</div>
            )}

            {filteredSymbols.length > 0 ? (
              filteredSymbols.map(([symbol, entry]) => (
                <div
                  key={symbol}
                  className={styles.dropdownItem}
                  onMouseDown={(event) => handleItemClick(event, symbol)}
                >
                  <div className={styles.dropdownItemRow}>
                    <SymbolLogo symbol={symbol} logoUrl={entry.logoUrl} size={16} />
                    <div className={styles.dropdownItemText}>
                      <span className={styles.symbolCode}>{symbol}</span>
                      <span className={styles.symbolName}>{entry.name}</span>
                      {entry.sectors.length > 0 && (
                        <span className={styles.symbolMeta}>{entry.sectors.join(" • ")}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.dropdownStatus}>No matching symbols found.</div>
            )}

            {allowCategorySelection && filteredCategories.length === 0 && searchTerm.trim() === "" && (
              <div className={styles.dropdownStatus}>No categories available.</div>
            )}

            {allowCategorySelection && filteredCategories.length > 0 && (
              <>
                <div className={styles.groupLabel}>Sectors / Industries</div>
                {filteredCategories.map((option) => (
                  <div
                    key={`category-${option.type}-${option.name}`}
                    className={`${styles.dropdownItem} ${styles.categoryItem}`}
                    onMouseDown={(event) => handleCategoryClick(event, option)}
                  >
                    <span className={styles.categoryLabel}>{option.name}</span>
                    <span className={styles.categoryMeta}>
                      {option.label} • {option.symbols.length} symbols
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};
